import React, { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { lessonDetails, removeLesson, saveLesson, lessonUsers, notifyUsers } from "../../services/lessons";
import Switch from "../../components/shared/switch";
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { SearchBar } from "../../components/FilterBar";

import ContentTranslation from "../../components/ContentTranslation";
import { filterEmptyTranslations } from "../../services/filters";
import { teamsList, getTeamGroups } from '../../services/teams';
import Table from "../../components/Table";
import ConfirmRemoveDialog from '../../components/ConfirmRemoveDialog';
import ReturnButton from '../../components/ReturnButton';

const LessonForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [language, setLanguage] = useState('en')
  const [lesson, setLesson] = useState({
    lesson_translations_attributes: [{
      title: '',
      content: '',
      locale: language,
      published: true
    }]
  })

  const loadData = async () => {
    if (!id) return;
    const { lesson } = await lessonDetails(id);
    setLesson(lesson);
  };

  useEffect(() => {loadData()}, [location.pathname])

  const save = async () => {
    if (!!lesson?.locked) {
      return;
    }

    const data = { ...lesson }
    data.lesson_translations_attributes = filterEmptyTranslations(lesson.lesson_translations_attributes)

    const response = await saveLesson(data);
    navigate("/club_admin/lessons/")
  }

  const remove = async () => {
    if (!lesson?.id || !!lesson?.locked) {
      return;
    }
    setShowConfirmDialog(true);
  }

  const confirmRemove = async () => {
    await removeLesson(lesson.id)
    navigate("/club_admin/lessons/")
  };

  return (
    <div className="form-container lesson-form">
      <ReturnButton />
      <div className="form-container-header">{t('lessons.edit')}</div>
      <div className="form-container-header">
        <div className="form-container-header-title flex1">
          {lesson?.title}
        </div>
        <div className="form-container-header-status">
          {t('lessons.published')}
          <Switch value={lesson?.published} onChange={(value) => setLesson({ ...lesson, published: value })} disabled={!!lesson?.locked}/>
        </div>
      </div>


      <div className="form-container-content">
        <ContentTranslation data={lesson?.lesson_translations_attributes}
                            disabled={!!lesson?.locked}
                            images={true}
                            onChange={values => setLesson({
                              ...lesson,
                              lesson_translations_attributes: values
                            })}/>
      </div>

      <div className="form-container-actions">
        <button className='button button-delete' disabled={!!lesson?.locked || !lesson?.id} onClick={remove}>{t('buttons.lesson.delete')}</button>
        <div className="flex-end">
          <button className="button" onClick={() => navigate("/club_admin/lessons/")}>{t('buttons.common.cancel')}</button>
          <button className="button button-primary" disabled={!!lesson?.locked} onClick={save}>{t('buttons.common.save-changes')}</button>
        </div>
      </div>

      <div>
        {id && <LessonUsers lessonId={id}/> }
      </div>

      {showConfirmDialog && (
        <ConfirmRemoveDialog
          title={t('lessons.remove_lesson', { title: lesson?.title })}
          confirm={confirmRemove}
          close={() => setShowConfirmDialog(false)}
        />
      )}
    </div>
  )
};

export default memo(LessonForm);

const LessonUsers = ({ lessonId }) => {
  const [{ users, count }, setData] = useState({ users: [], count: 0 })
  const [filters, setFilters] = useState({ page: 1, per_page: 10, user_type: 'all' })
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sending, setSending] = useState(false);
  const [notifiedUsers, setNotifiedUsers] = useState([]);

  const animatedComponents = makeAnimated();

  const { page, per_page, user_type } = filters;

  const { t } = useTranslation();

  const loadData = async () => {
    if (!lessonId) return;
    const { users, count } = await lessonUsers(lessonId, filters);
    setData({ users, count });
  };

  useEffect(() => {loadData()}, [lessonId, filters])

  const pagination = useMemo(
    () => ({
      pageIndex: page - 1,
      pageSize: per_page,
    }),
    [page, per_page]
  )

  const handlePagination = ({ pageSize, pageIndex}) => {
    setFilters({...filters, page: pageIndex + 1, per_page: pageSize })
  }

  const notify = async (items) => {
    const message = items.length > 1 ? t('lessons.notify_all') : t('lessons.notify_user', { name: items[0].name })
    if (!sending && confirm(message)) {
      setSending(true)
      const user_ids = items.filter(u => !notifiedUsers.includes(u.id) && !u.completed).map(u => u.id)
      try {
        await notifyUsers(lessonId, { user_ids })
        setNotifiedUsers(prev => [...prev, ...user_ids])
      } catch (error) { }

      setSending(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const { teams } = await teamsList();
      const { groups } = await getTeamGroups();
      const groupedTeams = groups.map(({ id, name }) => ({
        id,
        name,
        teams: teams.filter(t => t.team_group_id === id),
      }));
      groupedTeams.push({
        name: 'Teams',
        teams: teams.filter(t => !t.team_group_id),
      });
      setGroups(groupedTeams);
    };

    fetchData();
  }, []);

  const selectOptions = groups.map(({ name, teams }) => ({
    label: (
      <div
        onClick={() =>
          setSelectedOptions(value => [
            ...value,
            ...teams.flatMap(t => (value.some(v => v.value === t.id) ? [] : [{ value: t.id, label: t.name }])),
          ])
        }
      >
        {name}
      </div>
    ),
    options: teams.map(t => ({ value: t.id, label: t.name })),
  }));

  useEffect(() => {
    setFilters(prev => ({ ...prev, team_ids: selectedOptions.map(o => o.value) }));
  }, [selectedOptions]);

  const columns = useMemo(
    () => [
      { header: ' ', id: 'status', meta: { style: { width: 20, padding: 10 }}, cell: info =>
          <div className='flex-center'>
            {info.row.original?.completed && <i className="icon icon-check"></i>}
          </div>
      },
      { header:  t('common.table_name') , accessorKey: 'name', cell: info => info.getValue() },
      { header: t('common.table_email'), accessorKey: 'email', cell: info => info.getValue() },
      {
        id: 'notify',
        header: () => <button disabled={sending} className={`button button-outline-primary ${sending ? "disabled" : ""}`} onClick={() => notify(users)}>{t('buttons.lesson.notify')}</button>,
        cell: (info) =>
          <div className='flex-center'>
            { !info.row.original?.completed && (
              <>
                { notifiedUsers.includes(info.row.original?.id)
                 ? <i className="icon icon-check"></i> 
                 : <i className="icon icon-envelope-letter" onClick={() => notify([info.row.original])}></i> }
              </>
            ) }
          </div>, meta: { style: { width: 100, padding: 10 }}
      },
      { header: t('lessons.completed'), accessorKey: 'completed_at', cell: info => info.getValue(), meta: { style: { width: 110, padding: 10 }} },
    ],
    [sending, filters, users]
  )

  return (
    <div className="team-details">
      <div className="team-details-header">
        <div className="team-details-header-text">{t('lessons.title')}</div>
      </div>
      <div className="team-details-tabs">
        <div className={`team-details-tabs-option pointer ${user_type === "all" ? 'active' : ''}`} onClick={() => user_type !== 'all' && setFilters({...filters, user_type: 'all', page: 1 })}>{t('common.all')}</div>
        <div className={`team-details-tabs-option pointer ${user_type === "coach" ? 'active' : ''}`} onClick={() =>  user_type !== 'coach' && setFilters({...filters, user_type: 'coach', page: 1 })}>{t('common.coaches')}</div>
        <div className={`team-details-tabs-option pointer ${user_type === "player" ? 'active' : ''}`} onClick={() =>  user_type !== 'player' && setFilters({...filters, user_type: 'player', page: 1 })}>{t('common.players')}</div>
        <div className={`team-details-tabs-option pointer ${user_type === "viewer" ? 'active' : ''}`} onClick={() =>  user_type !== 'viewer' && setFilters({...filters, user_type: 'viewer', page: 1 })}>{t('common.parents')}</div>
      </div>
      <div className='filters-bar'>
        <SearchBar onChange={(q) => {setFilters({...filters, q})}} placeholder={t('lessons.placeholder')}/>
        <div className='filter1'>
        <Select
            closeMenuOnSelect={false}
            components={animatedComponents}
            isMulti
            options={selectOptions}
            styles={customStyles}
            placeholder={t('club.placeholder')}
            onChange={options => setSelectedOptions(options)}
            value={selectedOptions}
          />
        </div>
      </div>
      <div className="team-details-list">
        <Table {...{ data: users, columns, pageCount: Math.ceil(count / per_page), manualPagination: true, state: { pagination }, onPaginationChange: (e) => handlePagination(e(pagination)) }}/>
      </div>
    </div>

  )
}

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#F7F7F7',
    border: 'none',
    boxShadow: 'none',
    minWidth: '300px',
  }),
  multiValue: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: 'none',
    };
  },
};
